import React from 'react';
import PropTypes from 'prop-types';
import { LuTriangleAlert } from 'react-icons/lu';
import useEducationReportText from '../educationReportText';
import { OverdueCount, ProgressBar, STATUS_META, StatusBadge, initialsOf } from './ReportBits';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

/** Tooltip of a cell (the pending items behind a progress figure, …). */
export const useReportCellTitle = () => {
  const { label } = useEducationReportText();
  return (row, key) => {
    if (key === 'progress' || key === 'overdue') {
      const pending = row.items.filter((item) => !item.done && (key === 'progress' || item.overdue)).map((item) => item.name);
      return pending.length ? `${label('panel.pending')}: ${pending.join(', ')}` : undefined;
    }
    if (key === 'name') return row.name || undefined;
    if (key === 'role') return row.role || label('table.noRole');
    if (key === 'department') return row.department || undefined;
    if (key === 'project') return row.projects.join(', ') || undefined;
    if (key === 'email') return row.email || undefined;
    return undefined;
  };
};

/** Content of one report table cell. */
const ReportCell = ({ columnKey, row }) => {
  const { label } = useEducationReportText();
  switch (columnKey) {
    case 'name': {
      const meta = STATUS_META[row.status];
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${meta.gradient}`}>
            {initialsOf(row)}
          </span>
          <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.name || '-'}</span>
        </span>
      );
    }
    case 'role':
      return row.role ? <span className="tw-block tw-truncate">{row.role}</span> : <span className={`tw-italic ${MUTED}`}>{label('table.noRole')}</span>;
    case 'department':
      return row.department ? <span className="tw-block tw-truncate">{row.department}</span> : <span className={MUTED}>-</span>;
    case 'project':
      return row.projects.length ? <span className="tw-block tw-truncate">{row.projects.join(', ')}</span> : <span className={MUTED}>-</span>;
    case 'progress':
      return <ProgressBar done={row.done} total={row.total} status={row.status} />;
    case 'status':
      return <StatusBadge status={row.status} />;
    case 'sop':
      return row.sopTotal ? <span className="tw-tabular-nums">{row.sopDone}/{row.sopTotal}</span> : <span className={MUTED}>-</span>;
    case 'training':
      return row.eduTotal ? <span className="tw-tabular-nums">{row.eduDone}/{row.eduTotal}</span> : <span className={MUTED}>-</span>;
    case 'overdue':
      return <OverdueCount count={row.overdue} />;
    case 'email':
      return row.email ? <span className="tw-block tw-truncate">{row.email}</span> : <span className={MUTED}>-</span>;
    default:
      return null;
  }
};

ReportCell.propTypes = { columnKey: PropTypes.string.isRequired, row: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). `showProjects`: Eğitim Yönetimi. */
export const ReportCardRow = ({ row, showProjects }) => {
  const { label } = useEducationReportText();
  const meta = STATUS_META[row.status];
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${meta.gradient}`}>
        {initialsOf(row)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.name || '-'}</span>
          <StatusBadge status={row.status} />
        </span>
        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
          {[row.role || label('table.noRole'), ...(showProjects ? row.projects : [])].join(' · ')}
        </span>
        <span className="tw-mt-1.5 tw-flex tw-items-center tw-gap-2">
          <span className="tw-min-w-0 tw-flex-1"><ProgressBar done={row.done} total={row.total} status={row.status} /></span>
          {row.overdue > 0 && (
            <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400">
              <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {row.overdue}
            </span>
          )}
        </span>
      </span>
    </>
  );
};

ReportCardRow.propTypes = { row: PropTypes.object.isRequired, showProjects: PropTypes.bool };

export default ReportCell;
